import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { formatDateTime } from "@/lib/date";

const STATUS_CLS = {
  running: "bg-indigo-50 text-indigo-700 border-indigo-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

export default function AutomationRunsTab({ sources }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.adminAutoListRuns({ limit: 100 })
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const nameFor = (sid) => sources.find((s) => s.id === sid)?.name || "—";

  return (
    <section>
      <div className="mb-4">
        <h2 className="font-serif text-lg font-bold text-blueblack">Historial de ejecuciones</h2>
        <p className="text-xs text-textgray">Cada búsqueda manual o programada queda registrada acá con detección/nuevas/duplicadas/errores.</p>
      </div>

      <div className="bg-white border border-softblue overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lightgray/60 text-left text-[11px] uppercase tracking-widest text-textgray">
            <tr>
              <th className="px-4 py-3">Fuente</th>
              <th className="px-4 py-3">Inicio</th>
              <th className="px-4 py-3">Fin</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Detectadas</th>
              <th className="px-4 py-3 text-right">Nuevas</th>
              <th className="px-4 py-3 text-right">Duplicadas</th>
              <th className="px-4 py-3 text-right" title="Descartadas por filtro de fecha (ej: today_only)">Descartadas</th>
              <th className="px-4 py-3 text-right" title="Sin fecha confirmada — quedan en pestaña 'Fecha sin confirmar'">Sin fecha</th>
              <th className="px-4 py-3 text-right">Errores</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-textgray">
                <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-textgray">
                Todavía no hay ejecuciones registradas. Se poblará cuando el motor entre en operación.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-softblue/60">
                <td className="px-4 py-3">{nameFor(r.source_id)}</td>
                <td className="px-4 py-3 text-xs">{formatDateTime(r.started_at)}</td>
                <td className="px-4 py-3 text-xs">{r.finished_at ? formatDateTime(r.finished_at) : "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={STATUS_CLS[r.status] || ""}>{r.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.items_detected ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono">{r.items_new ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono">{r.items_duplicate ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-amber-700">{r.items_skipped_old ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-textgray">{r.items_skipped_no_date ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono">{r.items_error ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
